import axios from 'axios';
import * as XLSX from 'xlsx';
import { getCredentials, getConfig } from './woo';

/**
 * Servicio para sincronización de pagos con datos NAPSIS
 */

/**
 * Normaliza el nombre de un mes español y proporciona funciones de utilidad
 * @param {string} mes - Nombre del mes en español (ej: "marzo", "abril")
 * @returns {Object} Objeto con funciones de utilidad para el mes
 */
const normalizeMes = (mes) => {
  const mesMap = {
    'enero': 1, 'febrero': 2, 'marzo': 3, 'abril': 4,
    'mayo': 5, 'junio': 6, 'julio': 7, 'agosto': 8,
    'septiembre': 9, 'octubre': 10, 'noviembre': 11, 'diciembre': 12
  };
  
  const mesNormalizado = mes ? mes.toLowerCase().trim() : '';
  const numeroMes = mesMap[mesNormalizado] || 1;
  
  return {
    numero: numeroMes,
    nombre: mesNormalizado,
    yearMonth: (year) => {
      const mesStr = numeroMes.toString().padStart(2, '0');
      return `${year}-${mesStr}`;
    }
  };
};

/**
 * Parsea archivo NAPSIS (HTML o Excel) y extrae datos de estudiantes y pagos
 * @param {string|ArrayBuffer} content - Contenido del archivo NAPSIS
 * @param {string} fileType - Tipo de archivo ('html' o 'excel')
 * @param {Function} progressCallback - Callback para reporte de progreso
 * @returns {Array} Array de objetos con datos de estudiantes y sus pagos
 */
const parseNapsisData = (content, fileType = 'html', progressCallback = null) => {
  try {
    if (fileType === 'excel') {
      return parseExcelNapsisData(content, progressCallback);
    } else {
      return parseHtmlNapsisData(content, progressCallback);
    }
  } catch (error) {
    console.error('Error parseando datos NAPSIS:', error);
    throw new Error(`Error al procesar archivo NAPSIS: ${error.message}`);
  }
};

/**
 * Parsea archivo HTML de NAPSIS
 * @param {string} htmlContent - Contenido HTML del archivo NAPSIS
 * @param {Function} progressCallback - Callback para reporte de progreso
 * @returns {Array} Array de objetos con datos de estudiantes y sus pagos
 */
const parseHtmlNapsisData = (htmlContent, progressCallback = null) => {
  // Crear un parser DOM
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  
  // Buscar la tabla principal de datos
  const table = doc.querySelector('table.borderLightGrayTable');
  if (!table) {
    throw new Error('No se encontró la tabla de datos en el archivo NAPSIS');
  }
  
  const rows = table.querySelectorAll('tr');
  const students = [];
  
  let currentStudent = null;
  const totalRows = rows.length;
  
  for (let i = 2; i < rows.length; i++) { // Saltar headers
    const row = rows[i];
    const cells = row.querySelectorAll('td');
    
    if (cells.length === 0) continue;
    
    // Reporte de progreso
    if (progressCallback && i % 50 === 0) {
      progressCallback((i / totalRows) * 100);
    }
    
    // Verificar si es una nueva fila de estudiante (con total)
    const totalCell = cells[cells.length - 4]; // Columna "Total alumno"
    
    if (totalCell && totalCell.textContent.trim() !== '') {
      // Nueva fila de estudiante
      const nombre = cells[1]?.textContent.trim() || '';
      const rut = cells[2]?.textContent.trim() || '';
      
      if (rut && rut !== '') {
        currentStudent = {
          nombre: nombre,
          rut: rut.replace(/[.-]/g, ''), // Limpiar formato RUT
          totalAlumno: parseFloat(totalCell.textContent.replace(/[^\d.-]/g, '')) || 0,
          pagos: []
        };
        students.push(currentStudent);
      }
    }
    
    // Buscar datos de pago en la fila
    if (currentStudent) {
      // Buscar columnas de cuota y concepto
      for (let j = 0; j < cells.length; j++) {
        const cellText = cells[j]?.textContent.trim() || '';
        
        // Detectar formato "N : Mes" para cuotas
        const cuotaMatch = cellText.match(/(\d+)\s*:\s*(\w+)/);
        if (cuotaMatch) {
          const numeroCuota = cuotaMatch[1];
          const mes = cuotaMatch[2];
          
          // Buscar concepto y estado en las siguientes columnas
          const concepto = cells[j + 1]?.textContent.trim() || '';
          const estado = cells[j + 2]?.textContent.trim() || '';
          
          if (concepto && estado) {
            const isCompleted = estado.toLowerCase().includes('cancelado');
            
            console.log('📄 HTML Parse - Pago encontrado:', {
              numeroCuota,
              mes,
              concepto,
              estadoOriginal: estado,
              estadoDetected: isCompleted ? 'completed' : 'pending',
              includesCancelado: estado.toLowerCase().includes('cancelado')
            });
            
            currentStudent.pagos.push({
              numeroCuota: parseInt(numeroCuota),
              mes: mes,
              concepto: concepto,
              estado: isCompleted ? 'completed' : 'pending',
              estadoOriginal: estado
            });
          }
        }
      }
    }
  }
  
  if (progressCallback) progressCallback(100);
  console.log(`Parseados ${students.length} estudiantes desde NAPSIS HTML`);
  return students;
};

/**
 * Parsea archivo Excel de NAPSIS
 * @param {ArrayBuffer} excelBuffer - Buffer del archivo Excel
 * @param {Function} progressCallback - Callback para reporte de progreso
 * @returns {Array} Array de objetos con datos de estudiantes y sus pagos
 */
const parseExcelNapsisData = (excelBuffer, progressCallback = null) => {
  // Leer archivo Excel
  const workbook = XLSX.read(excelBuffer, { type: 'array' });
  
  // Obtener la primera hoja
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  
  // Convertir a JSON con headers
  const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
    header: 1, // Usar números como headers para acceso por índice
    defval: '', // Valor por defecto para celdas vacías
    blankrows: false // Omitir filas vacías
  });
  
  if (jsonData.length < 3) {
    throw new Error('El archivo Excel no contiene suficientes datos');
  }
  
  const students = [];
  let currentStudent = null;
  const totalRows = jsonData.length;
  
  // Detectar estructura de columnas automáticamente
  const headerRow = jsonData[1] || jsonData[0]; // Segunda fila o primera si no hay header
  console.log('📋 Excel Headers detected:', headerRow);
  
  const columnIndexes = detectExcelColumns(headerRow);
  console.log('📍 Column indexes detected:', columnIndexes);
  
  // Nueva estrategia: Agrupar filas por estudiante primero
  const studentGroups = new Map(); // rut -> {student, rows: []}
  
  for (let i = 2; i < jsonData.length; i++) {
    const row = jsonData[i];
    if (!row || row.length === 0) continue;
    
    // Reporte de progreso cada 100 filas
    if (progressCallback && i % 100 === 0) {
      progressCallback((i / totalRows) * 100);
    }
    
    const nombre = getCellValue(row, columnIndexes.nombre);
    const rut = getCellValue(row, columnIndexes.rut);
    const totalAlumno = getCellValue(row, columnIndexes.total);
    
    if (rut && rut.toString().trim() !== '') {
      const rutLimpio = rut.toString().replace(/[.-]/g, '');
      
      if (!studentGroups.has(rutLimpio)) {
        const student = {
          nombre: nombre ? nombre.toString().trim() : '',
          rut: rutLimpio,
          totalAlumno: parseFloat(totalAlumno?.toString().replace(/[^\d.-]/g, '')) || 0,
          pagos: []
        };
        studentGroups.set(rutLimpio, { student, rows: [] });
        students.push(student);
        currentStudent = student;
      }
      
      // Agregar esta fila al grupo del estudiante
      studentGroups.get(rutLimpio).rows.push(row);
    } else if (currentStudent) {
      // Fila adicional del mismo estudiante (sin RUT)
      const rutLimpio = currentStudent.rut;
      if (studentGroups.has(rutLimpio)) {
        studentGroups.get(rutLimpio).rows.push(row);
      }
    }
  }
  
  console.log(`📊 Agrupación completada: ${studentGroups.size} estudiantes encontrados`);
  
  // Procesar cada estudiante con su nueva lógica
  for (const [rut, { student, rows }] of studentGroups) {
    console.log(`🧮 Procesando estudiante: ${student.nombre} (${rows.length} filas)`);
    
    // Usar la nueva función para procesar boletas
    const pagosProcessed = processStudentBoletas(rows, student);
    student.pagos = pagosProcessed;
    
    console.log(`✅ ${student.nombre}: ${pagosProcessed.length} cuotas procesadas`);
  }
  
  if (progressCallback) progressCallback(100);
  
  // Log de ejemplo de datos parseados con información de boletas
  if (students.length > 0) {
    const exampleStudent = students[0];
    const pagosConBoleta = exampleStudent.pagos.filter(p => p.boleta && (p.boleta.numero || p.boleta.fecha));
    
    console.log('📊 Ejemplo de estudiante parseado desde Excel:', {
      nombre: exampleStudent.nombre,
      rut: exampleStudent.rut,
      totalPagos: exampleStudent.pagos.length,
      pagosConBoleta: pagosConBoleta.length,
      boletas: pagosConBoleta.map(p => ({
        cuota: p.numeroCuota,
        mes: p.mes,
        boleta: p.boleta?.numero || 'Sin número',
        fecha: p.boleta?.fecha || 'Sin fecha'
      }))
    });
  }
  
  return students;
};

/**
 * Detecta automáticamente las columnas del Excel NAPSIS basándose en la estructura real
 * @param {Array} headerRow - Fila de headers
 * @returns {Object} Índices de columnas detectadas
 */
const detectExcelColumns = (headerRow) => {
  // Estructura real del Excel NAPSIS identificada:
  const indexes = {
    nombre: 1,           // Columna B: "Nombre Alumno"
    rut: 2,              // Columna C: "Run Alumno" 
    totalAlumno: 3,      // Columna D: "Total alumno"
    numeroBoleta: 9,     // Columna J: "Nro" (número de boleta)
    rutRazonSocial: 10,  // Columna K: "Rut razón social"
    fechaPago: 11,       // Columna L: "Fecha" (puede ser número de serie Excel o texto)
    abono: 12,           // Columna M: "Abono"  
    interes: 13,         // Columna N: "Interés"
    totalCancelado: 14,  // Columna O: "Total cancelado"
    cuotaInfo: 15,       // Columna P: "Cuota : Fecha Vencimiento" (ej: "1 : Marzo")
    concepto: 17,        // Columna R: "Concepto"
    estado: 18           // Columna S: "Cancelado"
  };
  
  console.log('📍 Column indexes detected (estructura real NAPSIS):', indexes);
  
  return indexes;
};

/**
 * Obtiene valor de celda de forma segura
 * @param {Array} row - Fila de datos
 * @param {number} index - Índice de columna
 * @returns {string|null} Valor de la celda
 */
const getCellValue = (row, index) => {
  if (index < 0 || index >= row.length) return null;
  const value = row[index];
  return value !== undefined && value !== null ? value : null;
};

/**
 * Convierte fecha numérica de Excel a formato de fecha
 * @param {number|string} excelDate - Fecha como número de serie Excel o string
 * @returns {string|null} Fecha en formato dd-MMM-yyyy o null
 */
const convertExcelDate = (excelDate) => {
  if (!excelDate) return null;
  
  // Si ya es string en formato fecha, devolverlo tal como está
  if (typeof excelDate === 'string') {
    // Verificar si es formato NAPSIS (dd-MMM-yyyy)
    if (/^\d{1,2}-(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)-\d{2,4}$/i.test(excelDate)) {
      return excelDate;
    }
    // Verificar otros formatos de fecha como string
    if (/^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/.test(excelDate)) {
      return excelDate;
    }
  }
  
  // Si es número (número de serie de Excel)
  if (typeof excelDate === 'number') {
    try {
      // Excel cuenta días desde el 1 de enero de 1900 (con bug del año bisiesto)
      // JavaScript cuenta milisegundos desde el 1 de enero de 1970
      const excelEpoch = new Date(1900, 0, 1); // 1 de enero de 1900
      const millisecondsPerDay = 24 * 60 * 60 * 1000;
      
      // Ajustar por el bug del año bisiesto de Excel (1900 no era bisiesto pero Excel lo cuenta)
      let adjustedDays = excelDate - 1; // Excel cuenta desde 1, JavaScript desde 0
      if (excelDate > 59) { // Después del 28 de febrero de 1900
        adjustedDays -= 1; // Compensar el día extra que Excel cuenta erróneamente
      }
      
      const date = new Date(excelEpoch.getTime() + (adjustedDays * millisecondsPerDay));
      
      // Formatear a formato NAPSIS (dd-MMM-yyyy)
      const day = date.getDate();
      const monthNames = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 
                         'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day.toString().padStart(2, '0')}-${month}-${year}`;
    } catch (error) {
      console.warn('Error convirtiendo fecha Excel:', excelDate, error);
      return null;
    }
  }
  
  return null;
};

/**
 * Convierte fecha NAPSIS (dd-MMM-yy) a formato legible
 * @param {string} napsisDate - Fecha en formato NAPSIS (ej: "12-dic-24")
 * @returns {string} Fecha formateada (ej: "12/12/2024")
 */
/**
 * Convierte fecha serial de Excel a Date
 * @param {number} serial - Número serial de Excel
 * @returns {Date} Objeto Date
 */
const excelSerialToDate = (serial) => {
  // Excel epoch es 1 de enero de 1900, pero hay un bug de Excel que cuenta 1900 como año bisiesto
  const excelEpoch = new Date(1899, 11, 30); // 30 de diciembre de 1899
  const days = Math.floor(serial);
  const time = serial - days;
  
  const result = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
  
  // Agregar tiempo fraccional
  if (time > 0) {
    result.setTime(result.getTime() + time * 24 * 60 * 60 * 1000);
  }
  
  return result;
};

const formatNapsisDate = (napsisDate) => {
  if (!napsisDate) return napsisDate;
  
  const str = napsisDate.toString().trim();
  
  // ✅ NUEVO: Manejar formato serial de Excel primero
  if (/^\d{5}\.\d+$/.test(str)) {
    const serialNumber = parseFloat(str);
    if (serialNumber >= 40000 && serialNumber <= 50000) {
      const date = excelSerialToDate(serialNumber);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
  }
  
  const monthMap = {
    'ene': '01', 'feb': '02', 'mar': '03', 'abr': '04',
    'may': '05', 'jun': '06', 'jul': '07', 'ago': '08',
    'sep': '09', 'oct': '10', 'nov': '11', 'dic': '12'
  };
  
  // Formato NAPSIS principal: dd-MMM-yy o dd-MMM-yyyy
  let match = str.match(/^(\d{1,2})-(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)-(\d{2,4})$/i);
  if (match) {
    const [, day, monthStr, year] = match;
    const month = monthMap[monthStr.toLowerCase()];
    const fullYear = year.length === 2 ? '20' + year : year;
    return `${day.padStart(2, '0')}/${month}/${fullYear}`;
  }
  
  // Formato con otros separadores: dd/MMM/yyyy, dd.MMM.yyyy
  match = str.match(/^(\d{1,2})[\.\/](ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)[\.\/](\d{2,4})$/i);
  if (match) {
    const [, day, monthStr, year] = match;
    const month = monthMap[monthStr.toLowerCase()];
    const fullYear = year.length === 2 ? '20' + year : year;
    return `${day.padStart(2, '0')}/${month}/${fullYear}`;
  }
  
  // Formato con espacios: dd MMM yyyy
  match = str.match(/^(\d{1,2})\s+(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)\s+(\d{2,4})$/i);
  if (match) {
    const [, day, monthStr, year] = match;
    const month = monthMap[monthStr.toLowerCase()];
    const fullYear = year.length === 2 ? '20' + year : year;
    return `${day.padStart(2, '0')}/${month}/${fullYear}`;
  }
  
  // Formato numérico: dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy
  match = str.match(/^(\d{1,2})[-\.\/](\d{1,2})[-\.\/](\d{2,4})$/);
  if (match) {
    const [, day, month, year] = match;
    const fullYear = year.length === 2 ? '20' + year : year;
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${fullYear}`;
  }
  
  // Formato internacional: yyyy-mm-dd
  match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) {
    const [, year, month, day] = match;
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  }
  
  // ✅ NUEVO: Formato solo día-mes sin año: dd-MMM (asume año actual)
  match = str.match(/^(\d{1,2})-(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)$/i);
  if (match) {
    const [, day, monthStr] = match;
    const month = monthMap[monthStr.toLowerCase()];
    const currentYear = new Date().getFullYear();
    return `${day.padStart(2, '0')}/${month}/${currentYear}`;
  }
  
  // ✅ NUEVO: Formato día-mes numérico: dd-mm (asume año actual)
  match = str.match(/^(\d{1,2})-(\d{1,2})$/);
  if (match) {
    const [, day, month] = match;
    const currentYear = new Date().getFullYear();
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${currentYear}`;
  }
  
  // ✅ NUEVO: Extraer fecha de texto con formato: "texto dd-MMM-yyyy"
  match = str.match(/.*(\d{1,2})-(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)-?(\d{2,4})?/i);
  if (match) {
    const [, day, monthStr, year] = match;
    const month = monthMap[monthStr.toLowerCase()];
    const fullYear = year ? (year.length === 2 ? '20' + year : year) : new Date().getFullYear();
    return `${day.padStart(2, '0')}/${month}/${fullYear}`;
  }
  
  // Si no coincide con ningún formato, devolver tal como está
  return str;
};

/**
 * Verifica si ya existe una orden de multa para un mes específico
 * @param {Array} userOrders - Órdenes del usuario
 * @param {string} mes - Mes en español (ej: "abril")
 * @param {number} año - Año (ej: 2025)
 * @returns {boolean} True si ya existe multa para ese mes
 */
const hasFineOrderForMonth = (userOrders, mes, año) => {
  return userOrders.some(order => {
    const metaMes = order.meta_data?.find(m => m.key === 'mes')?.value;
    const metaTipo = order.meta_data?.find(m => m.key === 'tipo_orden')?.value;
    const metaCuotaOrigen = order.meta_data?.find(m => m.key === 'cuota_origen')?.value;
    
    // Verificar que sea una multa y para el mismo mes/año o cuota origen
    if (metaTipo === 'multa') {
      // Opción 1: Coincidir por mes y año
      if (metaMes && metaMes.toLowerCase().includes(mes.toLowerCase()) && metaMes.includes(año.toString())) {
        return true;
      }
      // Opción 2: Coincidir por cuota origen (más específico)
      if (metaCuotaOrigen && metaMes && metaMes.toLowerCase().includes(mes.toLowerCase())) {
        return true;
      }
    }
    
    return false;
  });
};

/**
 * Calcula multas basándose en fechas reales de vencimiento y pago
 * @param {string} mesCuota - Mes de la cuota en español
 * @param {string} fechaPago - Fecha de pago en formato NAPSIS
 * @param {number} numeroCuota - Número de cuota
 * @returns {Object} Información de multas calculadas
 */
const calcularMultas = (mesCuota, fechaPago, numeroCuota) => {
  if (!mesCuota || !fechaPago) {
    return { cantidadMultas: 0, razon: 'Sin datos suficientes' };
  }
  
  const fechaPagoDate = parseNapsisDate(fechaPago);
  if (!fechaPagoDate) {
    return { cantidadMultas: 0, razon: 'Fecha de pago inválida' };
  }
  
  // Mapear mes español a número
  const mesMap = {
    'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3,
    'mayo': 4, 'junio': 5, 'julio': 6, 'agosto': 7,
    'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
  };
  
  const mesCuotaNum = mesMap[mesCuota.toLowerCase()];
  if (mesCuotaNum === undefined) {
    return { cantidadMultas: 0, razon: 'Mes de cuota inválido' };
  }
  
  // Fecha de vencimiento: día 15 del mismo mes de la cuota
  // Determinar el año correcto (puede ser 2025 para cuotas de marzo-diciembre)
  let añoCuota = fechaPagoDate.getFullYear();
  
  // Si la cuota es de un mes anterior al mes de pago, probablemente es del mismo año
  // Si la cuota es de un mes posterior, podría ser del año anterior
  const mesActual = fechaPagoDate.getMonth();
  if (mesCuotaNum > mesActual && (mesCuotaNum - mesActual) > 6) {
    // Si el mes de la cuota está muy adelante, probablemente es del año anterior
    añoCuota = añoCuota - 1;
  } else if (mesCuotaNum < mesActual && (mesActual - mesCuotaNum) > 6) {
    // Si el mes de la cuota está muy atrás, probablemente es del año siguiente
    añoCuota = añoCuota + 1;
  }
  
  const fechaVencimiento = new Date(añoCuota, mesCuotaNum, 15);
  
  // Si se pagó antes o el día del vencimiento (15), no hay multa
  if (fechaPagoDate <= fechaVencimiento) {
    return { 
      cantidadMultas: 0, 
      razon: 'Pagado a tiempo',
      fechaVencimiento: fechaVencimiento.toLocaleDateString('es-CL'),
      fechaPagoObj: fechaPagoDate.toLocaleDateString('es-CL')
    };
  }
  
  // Calcular días de diferencia desde el vencimiento
  const diasDiferencia = Math.floor((fechaPagoDate - fechaVencimiento) / (1000 * 60 * 60 * 24));
  
  // Si pagó después del día 15 (desde el día 16 en adelante), calcular multas
  let cantidadMultas = 0;
  
  if (diasDiferencia > 0) {
    // Primera multa: desde el día 16 (1 día después del vencimiento)
    cantidadMultas = 1;
    
    // Multas adicionales: por cada mes completo de atraso
    const mesesCompletos = Math.floor(diasDiferencia / 30); // Aproximadamente cada 30 días = 1 mes adicional
    cantidadMultas += mesesCompletos;
  }
  
  const razonDetallada = diasDiferencia <= 30 
    ? `${diasDiferencia} día(s) de atraso` 
    : `${Math.floor(diasDiferencia / 30)} mes(es) y ${diasDiferencia % 30} día(s) de atraso`;
  
  return {
    cantidadMultas: cantidadMultas,
    razon: `${cantidadMultas} multa(s) - ${razonDetallada}`,
    fechaVencimiento: fechaVencimiento.toLocaleDateString('es-CL'),
    fechaPagoObj: fechaPagoDate.toLocaleDateString('es-CL'),
    mesesAtraso: Math.floor(diasDiferencia / 30),
    diasAtraso: diasDiferencia
  };
};

/**
 * Convierte fecha NAPSIS a objeto Date
 * @param {string} napsisDate - Fecha en formato NAPSIS (ej: "12-dic-24")
 * @returns {Date|null} Objeto Date o null si no se puede parsear
 */
const parseNapsisDate = (napsisDate) => {
  if (!napsisDate) return null;
  
  const str = napsisDate.toString().trim();
  
  const monthMap = {
    'ene': 0, 'feb': 1, 'mar': 2, 'abr': 3,
    'may': 4, 'jun': 5, 'jul': 6, 'ago': 7,
    'sep': 8, 'oct': 9, 'nov': 10, 'dic': 11
  };
  
  // Formato NAPSIS principal: dd-MMM-yy o dd-MMM-yyyy
  let match = str.match(/^(\d{1,2})-(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)-(\d{2,4})$/i);
  if (match) {
    const [, day, monthStr, year] = match;
    const month = monthMap[monthStr.toLowerCase()];
    const fullYear = year.length === 2 ? parseInt(year) + 2000 : parseInt(year);
    return new Date(fullYear, month, parseInt(day));
  }
  
  // Formato con otros separadores: dd/MMM/yyyy, dd.MMM.yyyy
  match = str.match(/^(\d{1,2})[\.\/](ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)[\.\/](\d{2,4})$/i);
  if (match) {
    const [, day, monthStr, year] = match;
    const month = monthMap[monthStr.toLowerCase()];
    const fullYear = year.length === 2 ? parseInt(year) + 2000 : parseInt(year);
    return new Date(fullYear, month, parseInt(day));
  }
  
  // Formato con espacios: dd MMM yyyy
  match = str.match(/^(\d{1,2})\s+(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)\s+(\d{2,4})$/i);
  if (match) {
    const [, day, monthStr, year] = match;
    const month = monthMap[monthStr.toLowerCase()];
    const fullYear = year.length === 2 ? parseInt(year) + 2000 : parseInt(year);
    return new Date(fullYear, month, parseInt(day));
  }
  
  // Formato numérico: dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy
  match = str.match(/^(\d{1,2})[-\.\/](\d{1,2})[-\.\/](\d{2,4})$/);
  if (match) {
    const [, day, month, year] = match;
    const fullYear = year.length === 2 ? parseInt(year) + 2000 : parseInt(year);
    return new Date(fullYear, parseInt(month) - 1, parseInt(day)); // month - 1 porque Date usa 0-11
  }
  
  // Formato internacional: yyyy-mm-dd
  match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)); // month - 1 porque Date usa 0-11
  }
  
  return null;
};


/**
 * Obtiene el estado actual de órdenes de un usuario por RUT
 * @param {string} rut - RUT del usuario sin formato
 * @returns {Promise<Array>} Array de órdenes del usuario
 */
const getUserOrdersByRut = async (rut) => {
  const credentials = getCredentials();
  const apiUrl = getConfig(credentials, "WP_API_URL") || "https://pagos.blaspascal.cl/wp-json";
  
  try {
    const response = await axios.get(`${apiUrl}/datofy/v1/user-orders-by-rut/${rut}`);
    return response.data || [];
  } catch (error) {
    console.error(`Error obteniendo órdenes para RUT ${rut}:`, error);
    return [];
  }
};

/**
 * Compara datos de NAPSIS con el estado actual del sistema
 * @param {Array} napsisData - Datos parseados de NAPSIS
 * @param {Array} customers - Lista de customers del sistema
 * @param {Function} onProgressCallback - Callback para reporte de progreso
 * @param {Object} options - Opciones de procesamiento
 * @returns {Promise<Object>} Resultado de la comparación
 */
const comparePaymentStatus = async (napsisData, customers, onProgressCallback = null, options = {}) => {
  // Opciones con valores por defecto
  const {
    maxProcessed = 50, // Procesar hasta N usuarios para vista previa
    maxSyncable = 10   // Límite de sincronizaciones para testing
  } = options;

  console.log('🚀 PaymentSync: Starting comparison:', {
    napsisStudents: napsisData.length,
    customers: customers.length,
    maxProcessed: maxProcessed === -1 ? 'TODOS' : maxProcessed,
    maxSyncable: maxSyncable === -1 ? 'TODOS' : maxSyncable,
    hasProgressCallback: typeof onProgressCallback === 'function'
  });
  const results = {
    matched: [],        // Usuarios encontrados y comparados
    notFound: [],       // RUTs de NAPSIS no encontrados en el sistema
    discrepancies: [],  // Diferencias encontradas
    syncNeeded: [],     // Usuarios que necesitan sincronización
    statistics: {
      totalNapsis: napsisData.length,
      totalMatched: 0,
      totalDiscrepancies: 0,
      pendingToComplete: 0,
      missingOrders: 0
    }
  };
  
  // Crear mapa de customers por RUT para búsqueda rápida
  const customerMap = new Map();
  customers.forEach(customer => {
    if (customer.username) {
      const cleanRut = customer.username.replace(/[.-]/g, '').toLowerCase();
      customerMap.set(cleanRut, customer);
    }
  });
  
  let syncableCount = 0;
  let processedCount = 0;
  
  for (const napsisStudent of napsisData) {
    console.log('🔍 PaymentSync: Processing student:', napsisStudent.nombre, napsisStudent.rut);
    
    const cleanRut = napsisStudent.rut.replace(/[.-]/g, '').toLowerCase();
    const customer = customerMap.get(cleanRut);
    
    if (!customer) {
      console.log('❌ PaymentSync: Customer not found for RUT:', cleanRut);
      results.notFound.push({
        rut: napsisStudent.rut,
        nombre: napsisStudent.nombre,
        pagos: napsisStudent.pagos.length
      });
      continue;
    }
    
    console.log('✅ PaymentSync: Customer found:', customer.first_name, customer.last_name);
    
    // Usuario encontrado, comparar órdenes
    const userOrders = await getUserOrdersByRut(customer.username);
    console.log('📋 PaymentSync: Retrieved orders for user:', userOrders.length);
    
    // Si no tiene órdenes, saltar
    if (!userOrders || userOrders.length === 0) {
      console.log('⏭️ PaymentSync: Skipping user with no orders:', customer.first_name);
      continue;
    }
    
    // Incrementar contador de procesados
    processedCount++;
    console.log(`📊 PaymentSync: Processing user ${processedCount}/${maxProcessed}:`, customer.first_name);
    
    // Verificar si tiene pagos que requieren sincronización (NAPSIS completed → Sistema pending)
    const hasSyncablePayments = napsisStudent.pagos.some(napsisPago => {
      if (napsisPago.estado !== 'completed') return false;
      
      const mesMap = {
        'enero': '01', 'febrero': '02', 'marzo': '03', 'abril': '04',
        'mayo': '05', 'junio': '06', 'julio': '07', 'agosto': '08',
        'septiembre': '09', 'octubre': '10', 'noviembre': '11', 'diciembre': '12'
      };
      
      const mesNumero = mesMap[napsisPago.mes.toLowerCase()];
      if (!mesNumero) return false;
      
      const currentYear = new Date().getFullYear();
      const targetMonth = `${currentYear}-${mesNumero}`;
      
      const systemOrder = userOrders.find(order => {
        const orderMeta = order.meta_data || [];
        const orderMes = orderMeta.find(meta => meta.key === 'mes')?.value;
        const orderConcepto = orderMeta.find(meta => meta.key === 'concepto')?.value;
        
        return orderMes === targetMonth && 
               orderConcepto && 
               orderConcepto.toLowerCase().includes(napsisPago.concepto.toLowerCase().slice(0, 10));
      });
      
      // Retorna true si hay orden pendiente que corresponde a pago completado en NAPSIS
      return systemOrder && systemOrder.status === 'pending';
    });
    
    console.log('🔍 PaymentSync: Syncable payments check:', {
      user: customer.first_name,
      hasSyncablePayments,
      napsisPagos: napsisStudent.pagos.length,
      completedPagos: napsisStudent.pagos.filter(p => p.estado === 'completed').length
    });
    
    // Procesar TODOS los usuarios, pero limitar solo las sincronizaciones
    let shouldProcessSync = true;
    if (hasSyncablePayments) {
      syncableCount++;
      if (maxSyncable !== -1 && syncableCount > maxSyncable) {
        console.log(`🔢 Limitando sincronizaciones a ${maxSyncable} para testing, pero continuando procesamiento para vista previa`);
        shouldProcessSync = false; // Continuar procesando pero sin agregar sincronizaciones
      }
    }
    
    const comparison = {
      customer: customer,
      napsisData: napsisStudent,
      systemOrders: userOrders,
      discrepancies: [],
      syncActions: []
    };
    
    // Comparar cada pago de NAPSIS con las órdenes del sistema
    for (const napsisPago of napsisStudent.pagos) {
      const mesMap = {
        'enero': '01', 'febrero': '02', 'marzo': '03', 'abril': '04',
        'mayo': '05', 'junio': '06', 'julio': '07', 'agosto': '08',
        'septiembre': '09', 'octubre': '10', 'noviembre': '11', 'diciembre': '12'
      };
      
      const mesNumero = mesMap[napsisPago.mes.toLowerCase()];
      if (!mesNumero) continue;
      
      // Buscar orden correspondiente en el sistema (año actual por defecto)
      const currentYear = new Date().getFullYear();
      const targetMonth = `${currentYear}-${mesNumero}`;
      
      const systemOrder = userOrders.find(order => {
        const orderMeta = order.meta_data || [];
        const orderMes = orderMeta.find(meta => meta.key === 'mes')?.value;
        const orderConcepto = orderMeta.find(meta => meta.key === 'concepto')?.value;
        
        return orderMes === targetMonth && 
               orderConcepto && 
               orderConcepto.toLowerCase().includes(napsisPago.concepto.toLowerCase().slice(0, 10));
      });
      
      if (!systemOrder) {
        // Orden faltante en el sistema
        comparison.discrepancies.push({
          type: 'missing_order',
          napsisPago: napsisPago,
          mes: targetMonth,
          message: `Pago en NAPSIS (${napsisPago.estado}) pero no existe orden en el sistema`
        });
        
        if (napsisPago.estado === 'completed' && shouldProcessSync) {
          comparison.syncActions.push({
            action: 'create_order',
            mes: targetMonth,  // ✅ YA TIENE EL CAMPO MES
            concepto: napsisPago.concepto,
            estado: 'completed',
            napsisPago: napsisPago
          });
          results.statistics.missingOrders++;
        }
        
        // ✅ CREAR MULTA CUANDO FALTA LA ORDEN: Solo si hay diferencias y el pago fue tardío
        if (napsisPago.estado === 'completed' && shouldProcessSync) {
          console.log(`🔍 DEBUG MULTA - Orden faltante para ${customer.first_name}:`, {
            mes: napsisPago.mes,
            fechaPago: napsisPago.boleta?.fechaFormateada || napsisPago.fechaPago,
            shouldProcessSync: shouldProcessSync,
            estado: napsisPago.estado
          });
          
          const multaInfo = calcularMultas(napsisPago.mes, napsisPago.boleta?.fechaFormateada || napsisPago.fechaPago, napsisPago.numeroCuota);
          
          console.log(`🔍 DEBUG MULTA - Resultado calcularMultas:`, multaInfo);
          
          if (multaInfo.cantidadMultas > 0) {
            // Verificar si ya existe una multa para este mes
            const currentYear = new Date().getFullYear();
            const yaExisteMulta = hasFineOrderForMonth(userOrders, napsisPago.mes, currentYear);
            
            console.log(`🔍 DEBUG MULTA - Verificación existencia:`, {
              yaExisteMulta: yaExisteMulta,
              mes: napsisPago.mes,
              year: currentYear,
              userOrdersCount: userOrders.length
            });
            
            if (!yaExisteMulta) {
              console.log(`🚨 Creando multa por orden faltante para ${customer.first_name}:`, {
                mes: napsisPago.mes,
                cantidadMultas: multaInfo.cantidadMultas,
                diasAtraso: multaInfo.diasAtraso,
                fechaPago: napsisPago.boleta?.fechaFormateada || napsisPago.fechaPago
              });
              
              comparison.syncActions.push({
                action: 'create_fine',
                mes: targetMonth,
                concepto: napsisPago.concepto,
                multaData: {
                  mes: napsisPago.mes,
                  numeroCuota: napsisPago.numeroCuota,
                  cantidadMultas: multaInfo.cantidadMultas,
                  diasAtraso: multaInfo.diasAtraso,
                  mesesAtraso: multaInfo.mesesAtraso,
                  fechaVencimiento: multaInfo.fechaVencimiento,
                  fechaPago: napsisPago.boleta?.fechaFormateada || napsisPago.fechaPago,
                  razon: multaInfo.razon
                },
                napsisPago: napsisPago
              });
            }
          }
        }
        
        // Agregar acción de multa si hay información de multas en los datos NAPSIS
        if (napsisPago.multas && napsisPago.multas.cantidad > 0 && shouldProcessSync) {
          comparison.syncActions.push({
            action: 'create_fine',
            mes: targetMonth,
            concepto: napsisPago.concepto,
            multaData: {
              mes: napsisPago.mes,
              cantidadMultas: napsisPago.multas.cantidad,
              mesesAtraso: napsisPago.multas.mesesAtraso,
              fechaVencimiento: napsisPago.multas.fechaVencimiento,
              fechaPago: napsisPago.multas.fechaPago,
              razon: napsisPago.multas.razon
            },
            napsisPago: napsisPago
          });
        }
      } else {
        // Orden existe, comparar estados
        const systemStatus = systemOrder.status;
        const napsisStatus = napsisPago.estado;
        
        if (systemStatus !== napsisStatus) {
          comparison.discrepancies.push({
            type: 'status_mismatch',
            systemOrder: systemOrder,
            napsisPago: napsisPago,
            mes: targetMonth,
            systemStatus: systemStatus,
            napsisStatus: napsisStatus,
            message: `Estado en sistema: ${systemStatus}, en NAPSIS: ${napsisStatus}`
          });
          
          if (systemStatus === 'pending' && napsisStatus === 'completed' && shouldProcessSync) {
            comparison.syncActions.push({
              action: 'update_status',
              orderId: systemOrder.id,
              mes: targetMonth,  // ✅ AGREGAR CAMPO MES PARA FILTRADO
              fromStatus: systemStatus,
              toStatus: 'completed',
              napsisPago: napsisPago
            });
            results.statistics.pendingToComplete++;
            
            // ✅ CREAR MULTA CUANDO HAY DESINCRONIZACIÓN: Solo si el pago fue tardío
            const multaInfo = calcularMultas(napsisPago.mes, napsisPago.boleta?.fechaFormateada || napsisPago.fechaPago, napsisPago.numeroCuota);
            
            if (multaInfo.cantidadMultas > 0) {
              // Verificar si ya existe una multa para este mes
              const currentYear = new Date().getFullYear();
              const yaExisteMulta = hasFineOrderForMonth(userOrders, napsisPago.mes, currentYear);
              
              if (!yaExisteMulta) {
                console.log(`🚨 Creando multa por desincronización para ${customer.first_name}:`, {
                  mes: napsisPago.mes,
                  cantidadMultas: multaInfo.cantidadMultas,
                  diasAtraso: multaInfo.diasAtraso,
                  fechaPago: napsisPago.boleta?.fechaFormateada || napsisPago.fechaPago
                });
                
                comparison.syncActions.push({
                  action: 'create_fine',
                  mes: targetMonth,
                  concepto: napsisPago.concepto,
                  multaData: {
                    mes: napsisPago.mes,
                    numeroCuota: napsisPago.numeroCuota,
                    cantidadMultas: multaInfo.cantidadMultas,
                    diasAtraso: multaInfo.diasAtraso,
                    mesesAtraso: multaInfo.mesesAtraso,
                    fechaVencimiento: multaInfo.fechaVencimiento,
                    fechaPago: napsisPago.boleta?.fechaFormateada || napsisPago.fechaPago,
                    razon: multaInfo.razon
                  },
                  napsisPago: napsisPago
                });
              }
            }
          }
        }
        
      }
    }
    
    comparison.hasDiscrepancies = comparison.discrepancies.length > 0;
    comparison.needsSync = comparison.syncActions.length > 0;
    
    if (comparison.hasDiscrepancies) {
      results.discrepancies.push(comparison);
      results.statistics.totalDiscrepancies++;
    }
    
    if (comparison.needsSync) {
      results.syncNeeded.push(comparison);
    }
    
    results.matched.push(comparison);
    results.statistics.totalMatched++;
    
    // Callback de progreso en tiempo real - SIEMPRE ejecutar para usuarios con órdenes
    if (onProgressCallback) {
      console.log('📡 PaymentSync: Calling progress callback:', {
        processed: results.statistics.totalMatched,
        total: napsisData.length,
        user: comparison.customer.first_name,
        hasDiscrepancies: comparison.hasDiscrepancies,
        needsSync: comparison.needsSync,
        hasCallback: typeof onProgressCallback === 'function'
      });
      
      onProgressCallback({
        type: 'user_processed',
        processed: results.statistics.totalMatched,
        total: napsisData.length,
        currentUser: comparison
      });
    } else {
      console.log('⚠️ PaymentSync: No progress callback provided');
    }
    
    // Limitar procesamiento total si está configurado (-1 = sin límite)
    if (maxProcessed !== -1 && processedCount >= maxProcessed) {
      console.log(`🔢 Limitando procesamiento total a ${maxProcessed} usuarios para vista previa`);
      break;
    }
  }
  
  console.log('Comparación completada:', results.statistics);
  return results;
};

/**
 * Ejecuta una petición HTTP con reintentos automáticos
 * @param {Function} requestFunction - Función que ejecuta la petición
 * @param {number} maxRetries - Número máximo de reintentos (default: 3)
 * @param {number} delay - Delay entre reintentos en ms (default: 1000)
 * @returns {Promise<Object>} Resultado de la petición
 */
const executeWithRetry = async (requestFunction, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await requestFunction();
      if (attempt > 1) {
        console.log(`✅ Petición exitosa en intento ${attempt}/${maxRetries}`);
      }
      return result;
    } catch (error) {
      lastError = error;
      const isNetworkError = error.message?.includes('Network Error') || 
                            error.message?.includes('ERR_NETWORK') ||
                            error.message?.includes('ERR_FAILED') ||
                            error.code === 'NETWORK_ERROR' ||
                            error.response?.status >= 500;
      
      console.log(`❌ Intento ${attempt}/${maxRetries} falló:`, {
        error: error.message,
        isNetworkError,
        status: error.response?.status
      });
      
      if (attempt < maxRetries && isNetworkError) {
        console.log(`🔄 Reintentando en ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 1.5; // Incrementar delay exponencialmente
      } else {
        break;
      }
    }
  }
  
  throw lastError;
};

/**
 * Ejecuta la sincronización de pagos basada en las acciones identificadas
 * @param {Array} syncActions - Acciones de sincronización a ejecutar
 * @param {Function} onProgress - Callback para reporte de progreso
 * @returns {Promise<Object>} Resultado de la sincronización
 */
const executeSyncActions = async (syncActions, onProgress = null) => {
  const credentials = getCredentials();
  const apiUrl = getConfig(credentials, "WP_API_URL") || "https://pagos.blaspascal.cl/wp-json";
  
  // Calcular total de acciones
  const totalActions = syncActions.reduce((total, comparison) => total + comparison.syncActions.length, 0);
  let processedActions = 0;
  
  const results = {
    success: [],
    failed: [],
    retryQueue: [], // Nueva cola de reintentos
    statistics: {
      ordersUpdated: 0,
      ordersCreated: 0,
      totalProcessed: 0,
      totalActions: totalActions,
      errors: 0,
      retried: 0
    }
  };
  
  // Función para reportar progreso
  const reportProgress = () => {
    if (onProgress) {
      onProgress({
        processed: processedActions,
        total: totalActions,
        percentage: Math.round((processedActions / totalActions) * 100),
        current: results.statistics
      });
    }
  };

  console.log(`🚀 Iniciando sincronización: ${totalActions} acciones a procesar`);
  reportProgress();

  for (const comparison of syncActions) {
    for (const action of comparison.syncActions) {
      const actionDescription = `${action.action} para ${comparison.customer.first_name} ${comparison.customer.last_name}`;
      console.log(`📤 Procesando (${processedActions + 1}/${totalActions}): ${actionDescription}`);
      
      try {
        let response;
        
        // Función para ejecutar la petición específica
        const executeRequest = async () => {
          if (action.action === 'update_status') {
            // Actualizar estado de orden existente
            const response = await axios.post(`${apiUrl}/datofy/v1/update-order-status`, {
              order_id: action.orderId,
              new_status: action.toStatus,
              sync_source: 'napsis'
            });
            
            if (response.data.success) {
              return {
                type: 'update',
                customer: comparison.customer,
                action: action,
                message: `Orden ${action.orderId} actualizada a ${action.toStatus}`,
                response: response.data
              };
            } else {
              throw new Error(response.data.message || 'Error actualizando orden');
            }
          
        } else if (action.action === 'create_order') {
          // Crear nueva orden con fecha histórica
          const requestData = {
            customer_id: comparison.customer.id,
            mes: action.mes,
            concepto: action.concepto,
            estado: action.estado,
            napsis_data: action.napsisPago,
            sync_source: 'napsis'
          };
          
          // Agregar fecha histórica si el pago está completado y tiene fecha de boleta
          if (action.estado === 'completed' && action.napsisPago && action.napsisPago.boleta) {
            const fechaBoleta = action.napsisPago.boleta.fechaFormateada || action.napsisPago.boleta.fecha;
            if (fechaBoleta) {
              requestData.historical_date = fechaBoleta;
              console.log(`📅 Enviando fecha histórica para orden: ${fechaBoleta} (${action.mes})`);
            }
          }
          
          const response = await axios.post(`${apiUrl}/datofy/v1/create-napsis-order`, requestData);
          
          if (response.data.success) {
            return {
              type: 'create',
              customer: comparison.customer,
              action: action,
              order_id: response.data.order_id,
              message: `Orden creada para ${action.mes}`,
              response: response.data
            };
          } else {
            throw new Error(response.data.message || 'Error creando orden');
          }
          
        } else if (action.action === 'create_fine') {
          // Crear multa por atraso
          const requestData = {
            customer_id: comparison.customer.id,
            mes: action.mes, // Usar formato YYYY-MM del targetMonth
            concepto: `Multa por atraso - ${action.multaData?.concepto || action.concepto}`,
            estado: 'completed',
            sync_source: 'napsis',
            meta_data: [
              { key: 'mes', value: action.mes }, // Usar formato YYYY-MM
              { key: 'concepto', value: `Multa por atraso - ${action.multaData?.concepto || action.concepto}` },
              { key: 'tipo_orden', value: 'multa' },
              { key: 'cantidad_multas', value: action.multaData?.cantidadMultas },
              { key: 'dias_atraso', value: action.multaData?.diasAtraso || 0 },
              { key: 'sync_source', value: 'napsis' }
            ],
            line_items: [{
              name: `Multa - ${action.mes}`,
              quantity: action.multaData?.cantidadMultas || 1
            }]
          };
          
          // Agregar fecha histórica si está disponible
          if (action.multaData?.fechaPago) {
            requestData.historical_date = action.multaData.fechaPago;
            console.log(`📅 Enviando fecha histórica para multa: ${action.multaData.fechaPago} (${action.multaData.mes})`);
          }
          
          const response = await axios.post(`${apiUrl}/datofy/v1/create-fine-direct`, requestData);
          
          if (response.data.success) {
            return {
              type: 'create_fine',
              customer: comparison.customer,
              action: action,
              order_id: response.data.order_id,
              message: `Multa creada para ${action.multaData?.mes}: ${action.multaData?.cantidadMultas} multa(s)`,
              response: response.data
            };
          } else {
            throw new Error(response.data.message || 'Error creando multa');
          }
        }
        
        throw new Error(`Acción no reconocida: ${action.action}`);
      };

      // Ejecutar la petición con reintentos
      const result = await executeWithRetry(executeRequest);
      
      // Procesar resultado exitoso
      results.success.push(result);
      
      if (result.type === 'update') {
        results.statistics.ordersUpdated++;
      } else if (result.type === 'create' || result.type === 'create_fine') {
        results.statistics.ordersCreated++;
      }
      
      results.statistics.totalProcessed++;
      processedActions++;
      reportProgress();
      
      console.log(`✅ Éxito (${processedActions}/${totalActions}): ${result.message}`);
        
      } catch (error) {
        processedActions++;
        
        const isNetworkError = error.message?.includes('Network Error') || 
                              error.message?.includes('ERR_NETWORK') ||
                              error.message?.includes('ERR_FAILED') ||
                              error.code === 'NETWORK_ERROR' ||
                              error.response?.status >= 500;
        
        console.error(`❌ Error (${processedActions}/${totalActions}): ${actionDescription}`, {
          error: error.message,
          isNetworkError,
          status: error.response?.status
        });
        
        const failedItem = {
          customer: comparison.customer,
          action: action,
          error: error.message,
          isNetworkError: isNetworkError,
          description: actionDescription
        };
        
        if (isNetworkError) {
          // Agregar a cola de reintentos para el final
          results.retryQueue.push(failedItem);
          console.log(`🔄 Agregado a cola de reintentos: ${actionDescription}`);
        } else {
          // Error definitivo (no de red)
          results.failed.push(failedItem);
        }
        
        results.statistics.errors++;
        reportProgress();
      }
    }
  }

  // FASE 2: Procesar cola de reintentos al final
  if (results.retryQueue.length > 0) {
    console.log(`🔄 Procesando ${results.retryQueue.length} peticiones de la cola de reintentos...`);
    
    for (let i = 0; i < results.retryQueue.length; i++) {
      const retryItem = results.retryQueue[i];
      const action = retryItem.action;
      const comparison = { customer: retryItem.customer, syncActions: [action] };
      
      console.log(`🔄 Reintento final (${i + 1}/${results.retryQueue.length}): ${retryItem.description}`);
      
      try {
        const executeRequest = async () => {
          if (action.action === 'update_status') {
            const response = await axios.post(`${apiUrl}/datofy/v1/update-order-status`, {
              order_id: action.orderId,
              new_status: action.toStatus,
              sync_source: 'napsis'
            });
            
            if (response.data.success) {
              return {
                type: 'update',
                customer: retryItem.customer,
                action: action,
                message: `Orden ${action.orderId} actualizada a ${action.toStatus} (reintento)`,
                response: response.data,
                wasRetried: true
              };
            } else {
              throw new Error(response.data.message || 'Error actualizando orden');
            }
          } else if (action.action === 'create_order') {
            const requestData = {
              customer_id: retryItem.customer.id,
              mes: action.mes,
              concepto: action.concepto,
              estado: action.estado,
              napsis_data: action.napsisPago,
              sync_source: 'napsis'
            };
            
            if (action.estado === 'completed' && action.napsisPago && action.napsisPago.boleta) {
              const fechaBoleta = action.napsisPago.boleta.fechaFormateada || action.napsisPago.boleta.fecha;
              if (fechaBoleta) {
                requestData.historical_date = fechaBoleta;
              }
            }
            
            const response = await axios.post(`${apiUrl}/datofy/v1/create-napsis-order`, requestData);
            
            if (response.data.success) {
              return {
                type: 'create',
                customer: retryItem.customer,
                action: action,
                order_id: response.data.order_id,
                message: `Orden creada para ${action.mes} (reintento)`,
                response: response.data,
                wasRetried: true
              };
            } else {
              throw new Error(response.data.message || 'Error creando orden');
            }
          } else if (action.action === 'create_fine') {
            const requestData = {
              customer_id: retryItem.customer.id,
              mes: action.mes,
              concepto: `Multa por atraso - ${action.multaData?.concepto || action.concepto}`,
              estado: 'completed',
              sync_source: 'napsis',
              meta_data: [
                { key: 'mes', value: action.mes },
                { key: 'concepto', value: `Multa por atraso - ${action.multaData?.concepto || action.concepto}` },
                { key: 'tipo_orden', value: 'multa' },
                { key: 'cantidad_multas', value: action.multaData?.cantidadMultas },
                { key: 'dias_atraso', value: action.multaData?.diasAtraso || 0 },
                { key: 'sync_source', value: 'napsis' }
              ],
              line_items: [{
                name: `Multa - ${action.mes}`,
                quantity: action.multaData?.cantidadMultas || 1
              }]
            };
            
            if (action.multaData?.fechaPago) {
              requestData.historical_date = action.multaData.fechaPago;
            }
            
            const response = await axios.post(`${apiUrl}/datofy/v1/create-fine-direct`, requestData);
            
            if (response.data.success) {
              return {
                type: 'create_fine',
                customer: retryItem.customer,
                action: action,
                order_id: response.data.order_id,
                message: `Multa creada para ${action.multaData?.mes}: ${action.multaData?.cantidadMultas} multa(s) (reintento)`,
                response: response.data,
                wasRetried: true
              };
            } else {
              throw new Error(response.data.message || 'Error creando multa');
            }
          }
          
          throw new Error(`Acción no reconocida: ${action.action}`);
        };

        // Ejecutar reintento con retry más agresivo
        const retryResult = await executeWithRetry(executeRequest, 5, 2000);
        
        // Éxito en reintento
        results.success.push(retryResult);
        
        if (retryResult.type === 'update') {
          results.statistics.ordersUpdated++;
        } else if (retryResult.type === 'create' || retryResult.type === 'create_fine') {
          results.statistics.ordersCreated++;
        }
        
        results.statistics.totalProcessed++;
        results.statistics.retried++;
        
        console.log(`✅ Reintento exitoso: ${retryResult.message}`);
        
      } catch (retryError) {
        // Falló definitivamente
        retryItem.retryError = retryError.message;
        results.failed.push(retryItem);
        console.error(`❌ Reintento falló definitivamente: ${retryItem.description}`, retryError.message);
      }
    }
    
    // Limpiar cola de reintentos
    results.retryQueue = [];
  }
  
  console.log('Sincronización completada:', results.statistics);
  return results;
};

/**
 * Obtiene estadísticas generales de sincronización
 * @returns {Promise<Object>} Estadísticas del sistema
 */
const getSyncStatistics = async () => {
  const credentials = getCredentials();
  const apiUrl = getConfig(credentials, "WP_API_URL") || "https://pagos.blaspascal.cl/wp-json";
  
  try {
    const response = await axios.get(`${apiUrl}/datofy/v1/sync-statistics`);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo estadísticas:', error);
    return {
      totalOrders: 0,
      pendingOrders: 0,
      completedOrders: 0,
      lastSyncDate: null
    };
  }
};

/**
 * Valida formato de archivo NAPSIS
 * @param {File} file - Archivo a validar
 * @returns {Promise<boolean>} True si el archivo es válido
 */
const validateNapsisFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        
        // Verificar que contenga elementos esperados de NAPSIS
        const hasTable = content.includes('borderLightGrayTable');
        const hasStudentData = content.includes('Run Alumno') || content.includes('Alumno');
        const hasBillingData = content.includes('Boletas') || content.includes('Cancelado');
        
        if (hasTable && hasStudentData && hasBillingData) {
          resolve(true);
        } else {
          reject(new Error('El archivo no parece ser un reporte válido de NAPSIS'));
        }
      } catch (error) {
        reject(new Error('Error validando archivo: ' + error.message));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Error leyendo archivo'));
    };
    
    reader.readAsText(file, 'UTF-8');
  });
};

/**
 * Procesa las filas de un estudiante para agrupar cuotas por boleta
 * @param {Array} studentRows - Todas las filas del estudiante
 * @param {Object} student - Objeto del estudiante
 * @returns {Array} Pagos procesados con boletas agrupadas
 */
const processStudentBoletas = (studentRows, student) => {
  const pagos = [];
  const boletasDetectadas = new Map(); // numero -> {fecha, total, cuotas: []}
  const columns = detectExcelColumns(); // Obtener mapeo de columnas real
  
  console.log(`🧮 Procesando boletas para: ${student.nombre} (${studentRows.length} filas)`);
  
  // Primera pasada: detectar todas las boletas únicas
  studentRows.forEach((row, index) => {
    const START_COLUMN = 15;
    
    for (let j = START_COLUMN; j < row.length; j++) {
      const cellValue = getCellValue(row, j);
      if (!cellValue) continue;
      
      const cuotaMatch = cellValue.toString().match(/(\d+)\s*:\s*(\w+)/);
      if (cuotaMatch) {
        // Buscar datos de boleta en toda la fila
        const posiblesBoleta = [];
        for (let k = 0; k < row.length; k++) {
          const val = getCellValue(row, k);
          if (val !== null) {
            posiblesBoleta.push(val);
          }
        }
        
        // Detectar número de boleta
        const nroBoleta = posiblesBoleta.find(val => {
          if (!val) return false;
          const str = val.toString().trim();
          return /^\d{5,6}$/.test(str) && !str.includes('/') && !str.includes('-');
        });
        
        // Detectar fecha de pago - múltiples formatos MEJORADO
        const fechaPago = posiblesBoleta.find(val => {
          if (!val) return false;
          const str = val.toString().trim();
          
          // ✅ NUEVO: Formato serial de Excel (ej: 45824.999479167 = fecha Excel)
          // Excel serial dates están típicamente entre 40000-50000 para fechas 2009-2037
          if (/^\d{5}\.\d+$/.test(str)) {
            const serialNumber = parseFloat(str);
            if (serialNumber >= 40000 && serialNumber <= 50000) {
              return true;
            }
          }
          
          // Formato NAPSIS principal: dd-MMM-yy o dd-MMM-yyyy (ej: "24-Abr-2025", "12-dic-24")
          if (/^\d{1,2}-(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)-\d{2,4}$/i.test(str)) {
            return true;
          }
          
          // Formato con separadores diferentes: dd/MMM/yyyy, dd.MMM.yyyy
          if (/^\d{1,2}[\.\/](ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)[\.\/]\d{2,4}$/i.test(str)) {
            return true;
          }
          
          // Formato numérico: dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy
          if (/^\d{1,2}[-\.\/]\d{1,2}[-\.\/]\d{2,4}$/.test(str)) {
            return true;
          }
          
          // Formato internacional: yyyy-mm-dd
          if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(str)) {
            return true;
          }
          
          // Formato con espacios: dd MMM yyyy (ej: "24 Abr 2025")
          if (/^\d{1,2}\s+(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)\s+\d{2,4}$/i.test(str)) {
            return true;
          }
          
          // ✅ NUEVO: Formato solo día-mes sin año: dd-MMM (ej: "24-May", "15-Jun")
          if (/^\d{1,2}-(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)$/i.test(str)) {
            return true;
          }
          
          // ✅ NUEVO: Formato día-mes numérico: dd-mm (ej: "24-05", "15-06") 
          if (/^\d{1,2}-\d{1,2}$/.test(str)) {
            return true;
          }
          
          // ✅ NUEVO: Formato con texto adicional: "Pagado el dd-MMM-yyyy" o similar
          if (/(pagado|fecha|pago).*\d{1,2}-(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)-?\d{0,4}/i.test(str)) {
            return true;
          }
          
          return false;
        });
        
        // Detectar total
        const totalBoleta = posiblesBoleta.find(val => {
          if (!val) return false;
          const str = val.toString().trim();
          const isDifferentFromBoleta = nroBoleta ? str !== nroBoleta.toString() : true;
          return /^\d{4,}$/.test(str) && isDifferentFromBoleta;
        });
        
        if (nroBoleta) {
          // Si la boleta ya existe, actualizar con nuevos datos si están disponibles
          if (boletasDetectadas.has(nroBoleta.toString())) {
            const boletaExistente = boletasDetectadas.get(nroBoleta.toString());
            
            // ✅ ACTUALIZAR FECHA si no la tenía y ahora sí la encontramos
            if (!boletaExistente.fecha && fechaPago) {
              boletaExistente.fecha = fechaPago.toString().trim();
              boletaExistente.fechaFormateada = formatNapsisDate(fechaPago.toString().trim());
              console.log(`📅 Actualizando fecha para boleta existente: ${nroBoleta} - Nueva fecha: ${fechaPago}`);
            }
            
            // ✅ ACTUALIZAR TOTAL si no lo tenía y ahora sí lo encontramos
            if (!boletaExistente.total && totalBoleta) {
              boletaExistente.total = totalBoleta.toString().trim();
              console.log(`💰 Actualizando total para boleta existente: ${nroBoleta} - Nuevo total: ${totalBoleta}`);
            }
          } else {
            // Nueva boleta detectada
            // Debug ampliado para problemas de fecha
            if (nroBoleta.toString() === '24208' || !fechaPago) {
              console.log(`🔍 DEBUG Boleta ${nroBoleta} (Sin fecha detectada):`, {
                filaCompleta: posiblesBoleta,
                fechaDetectada: fechaPago,
                totalDetectado: totalBoleta,
                posiblesFechas: posiblesBoleta.filter(val => {
                  if (!val) return false;
                  const str = val.toString().trim();
                  // Mostrar cualquier valor que tenga guiones, puntos o barras (posibles fechas)
                  return str.includes('-') || str.includes('/') || str.includes('.') || 
                         /\d{1,2}.*\d{2,4}/.test(str);
                }),
                fechaFormateada: fechaPago ? formatNapsisDate(fechaPago.toString().trim()) : 'NO DETECTADA'
              });
            }
            
            boletasDetectadas.set(nroBoleta.toString(), {
              numero: nroBoleta.toString(),
              fecha: fechaPago ? fechaPago.toString().trim() : null,
              fechaFormateada: fechaPago ? formatNapsisDate(fechaPago.toString().trim()) : null,
              total: totalBoleta ? totalBoleta.toString().trim() : null,
              cuotas: [],
              filaInicio: index
            });
            
            console.log(`\n💰 NUEVA BOLETA DETECTADA:`);
            console.log(`   Número: ${nroBoleta}`);
            console.log(`   Fecha: ${fechaPago || 'NO DETECTADA'}`);
            console.log(`   Total: ${totalBoleta || 'NO DETECTADO'}`);
            console.log(`   Fila: ${index}`);
            console.log(`   Datos completos fila:`, posiblesBoleta.slice(0, 15));
          }
        }
      }
    }
  });
  
  // Segunda pasada: asignar cuotas a boletas
  studentRows.forEach((row, rowIndex) => {
    const START_COLUMN = 15;
    
    for (let j = START_COLUMN; j < row.length; j++) {
      const cellValue = getCellValue(row, j);
      if (!cellValue) continue;
      
      const cuotaMatch = cellValue.toString().match(/(\d+)\s*:\s*(\w+)/);
      if (cuotaMatch) {
        const numeroCuota = cuotaMatch[1];
        const mes = cuotaMatch[2];
        
        // Buscar concepto y estado en columnas posteriores
        const concepto = getCellValue(row, j + 2) || '';
        const estado = getCellValue(row, j + 3) || '';
        
        // Filtrar primer arancel
        if (concepto.toLowerCase().includes('primer arancel') || concepto.toLowerCase().includes('preb')) {
          console.log(`⚠️ Cuota descartada: ${numeroCuota} : ${mes} - ${concepto}`);
          continue;
        }
        
        // ✅ NUEVA LÓGICA: Asociar boleta basándose en la MISMA FILA donde está la cuota
        let boletaCercana = null;
        
        // Buscar número de boleta EN LA MISMA FILA de la cuota
        const posiblesBoleta = [];
        for (let k = 0; k < row.length; k++) {
          const val = getCellValue(row, k);
          if (val !== null) {
            posiblesBoleta.push(val);
          }
        }
        
        // Detectar número de boleta en esta fila específica
        const nroBoletaEnFila = posiblesBoleta.find(val => {
          if (!val) return false;
          const str = val.toString().trim();
          return /^\d{5,6}$/.test(str) && !str.includes('/') && !str.includes('-');
        });
        
        // 🔍 LOG SIMPLE PARA DEBUG
        console.log(`\n📋 FILA ${rowIndex}: Cuota ${numeroCuota}-${mes}`);
        console.log(`   Datos en fila:`, posiblesBoleta.slice(0, 10)); // Solo primeros 10 valores
        console.log(`   Boleta detectada en fila: ${nroBoletaEnFila || 'NINGUNA'}`);
        console.log(`   Boletas disponibles:`, Array.from(boletasDetectadas.keys()));
        
        // Si encontramos número de boleta en la misma fila, usar esa boleta
        if (nroBoletaEnFila && boletasDetectadas.has(nroBoletaEnFila.toString())) {
          boletaCercana = boletasDetectadas.get(nroBoletaEnFila.toString());
          console.log(`   ✅ RESULTADO: Cuota ${numeroCuota}-${mes} → Boleta ${nroBoletaEnFila} (MISMA FILA)`);
        } else {
          // ✅ NUEVA LÓGICA: Buscar boleta de la cuota anterior si es consecutiva
          const numeroActual = parseInt(numeroCuota);
          const cuotaAnterior = pagos.find(p => parseInt(p.numeroCuota) === numeroActual - 1);
          
          if (cuotaAnterior && cuotaAnterior.boleta) {
            // Verificar si la boleta anterior tiene suficiente monto para esta cuota también
            const boletaAnterior = boletasDetectadas.get(cuotaAnterior.boleta.numero);
            const montoBoletaAnterior = parseFloat(boletaAnterior.total) || 0;
            const montoCuotaPromedio = 90000; // Estimación de cuota mensual
            
            if (montoBoletaAnterior > montoCuotaPromedio * 1.5) { // Si boleta >1.5 cuotas
              boletaCercana = boletaAnterior;
              console.log(`   🔗 RESULTADO: Cuota ${numeroCuota}-${mes} → Boleta ${boletaAnterior.numero} (CONSECUTIVA - Boleta compartida)`);
            } else {
              // Fallback: buscar por proximidad
              let menorDistancia = Infinity;
              for (const [numero, boleta] of boletasDetectadas) {
                const distancia = Math.abs(rowIndex - boleta.filaInicio);
                if (distancia < menorDistancia) {
                  menorDistancia = distancia;
                  boletaCercana = boleta;
                }
              }
              console.log(`   ⚠️ RESULTADO: Cuota ${numeroCuota}-${mes} → Boleta ${boletaCercana?.numero} (PROXIMIDAD - ${menorDistancia} filas)`);
            }
          } else {
            // Fallback original: buscar por proximidad
            let menorDistancia = Infinity;
            for (const [numero, boleta] of boletasDetectadas) {
              const distancia = Math.abs(rowIndex - boleta.filaInicio);
              if (distancia < menorDistancia) {
                menorDistancia = distancia;
                boletaCercana = boleta;
              }
            }
            console.log(`   ⚠️ RESULTADO: Cuota ${numeroCuota}-${mes} → Boleta ${boletaCercana?.numero} (PROXIMIDAD - ${menorDistancia} filas)`);
          }
        }
        
        if (boletaCercana) {
          // ✅ BUSCAR FECHA EN LA MISMA FILA SI LA BOLETA NO TIENE FECHA
          if (!boletaCercana.fecha || !boletaCercana.fechaFormateada) {
            // Detectar fecha de pago en la misma fila de la cuota
            const fechaEnFila = posiblesBoleta.find(val => {
              if (!val) return false;
              const str = val.toString().trim();
              
              // Formato serial de Excel (ej: 45824.999479167 = fecha Excel)
              if (/^\d{5}\.\d+$/.test(str)) {
                const serialNumber = parseFloat(str);
                if (serialNumber >= 40000 && serialNumber <= 50000) {
                  return true;
                }
              }
              
              // Formatos de fecha normales
              if (/^\d{1,2}-(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)-\d{2,4}$/i.test(str)) return true;
              if (/^\d{1,2}[\.\/](ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)[\.\/]\d{2,4}$/i.test(str)) return true;
              if (/^\d{1,2}[-\.\/]\d{1,2}[-\.\/]\d{2,4}$/.test(str)) return true;
              if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(str)) return true;
              
              return false;
            });
            
            if (fechaEnFila) {
              boletaCercana.fecha = fechaEnFila.toString().trim();
              boletaCercana.fechaFormateada = formatNapsisDate(fechaEnFila.toString().trim());
              console.log(`📅 Fecha encontrada en fila de cuota para boleta ${boletaCercana.numero}: ${fechaEnFila} → ${boletaCercana.fechaFormateada}`);
            }
          }
          
          // ✅ ASEGURAR QUE LA FECHA ESTÉ DISPONIBLE
          // Si la boleta no tiene fecha formateada pero sí tiene fecha original, formatearla
          if (!boletaCercana.fechaFormateada && boletaCercana.fecha) {
            boletaCercana.fechaFormateada = formatNapsisDate(boletaCercana.fecha);
            console.log(`📅 Formateando fecha faltante para boleta ${boletaCercana.numero}: ${boletaCercana.fecha} → ${boletaCercana.fechaFormateada}`);
          }
          
          // Calcular multas para esta cuota específica
          const multasInfo = calcularMultas(mes, boletaCercana.fecha, numeroCuota);
          
          const cuotaData = {
            numeroCuota: numeroCuota,
            mes: mes,
            concepto: concepto.toString().trim(),
            estado: 'completed',
            estadoOriginal: estado.toString().trim(),
            boleta: {
              numero: boletaCercana.numero,
              fecha: boletaCercana.fecha,
              fechaFormateada: boletaCercana.fechaFormateada || (boletaCercana.fecha ? formatNapsisDate(boletaCercana.fecha) : null),
              total: boletaCercana.total
            },
            multas: {
              cantidad: multasInfo.cantidadMultas,
              razon: multasInfo.razon,
              fechaVencimiento: multasInfo.fechaVencimiento,
              fechaPago: multasInfo.fechaPagoObj,
              mesesAtraso: multasInfo.mesesAtraso || 0
            }
          };
          
          pagos.push(cuotaData);
          boletaCercana.cuotas.push(cuotaData);
          
          console.log(`✅ Cuota procesada: ${numeroCuota} - ${mes} → Boleta: ${boletaCercana.numero}`);
        }
      }
    }
  });
  
  console.log(`📊 Resumen ${student.nombre}: ${boletasDetectadas.size} boletas, ${pagos.length} cuotas`);
  return pagos;
};

export default {
  parseNapsisData,
  getUserOrdersByRut,
  comparePaymentStatus,
  executeSyncActions,
  getSyncStatistics,
  validateNapsisFile,
  normalizeMes,
  hasFineOrderForMonth,
  calcularMultas
};